/**
 * useSalesAppointmentsData Hook
 *
 * Fetches the 5-day VinSolutions appointment outlook for the currently
 * selected location on the Forecast page. Backs the "Sales Appointments —
 * 5-day outlook" subsection inside the Sales Department Analysis card.
 *
 * The same data powers CEO Daily Brief Section 7; this hook hits a thin
 * per-location endpoint that wraps the same backend helper.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSalesAppointments, SalesAppointmentsResponse } from '../services/api';

interface UseSalesAppointmentsDataParams {
  locationId: number;
  enabled: boolean;
}

interface UseSalesAppointmentsDataResult {
  data: SalesAppointmentsResponse | null;
  loading: boolean;
  error: string | null;
}

export function useSalesAppointmentsData({
  locationId,
  enabled,
}: UseSalesAppointmentsDataParams): UseSalesAppointmentsDataResult {
  const [data, setData] = useState<SalesAppointmentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSalesAppointments(locationId);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Failed to load sales appointments data');
        setData(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setError(null);
      return;
    }
    fetchData();
  }, [locationId, enabled, fetchData]);

  return { data, loading, error };
}
